import styles from './RankedListRow.module.css'

/**
 * RankedListRow — one row of a ranked-opportunity list: a rank circle, a
 * label, a progress underline, and a trailing value or text badge.
 *
 * Shared by the `rankedList` block (static, inside an insight card's hero)
 * and InsightDetailModal's left-rail tabs (clickable, with an active state)
 * — both need the exact same row visual, just with/without interaction.
 *
 * Props:
 *   rank     {number}
 *   label    {string}
 *   progress {number} — 0-100, underline fill. Omitted rows show no bar.
 *   value    {number|string} — trailing numeric value
 *   badge    {string} — trailing text (not a pill) instead of `value` (e.g. "Assist")
 *   valueColor {string} — optional CSS color for the trailing value/badge text
 *   color    {string} — optional CSS color override for the progress fill
 *                        and the rank circle badge. The badge stays an
 *                        outlined ring (transparent fill, tinted border and
 *                        text) until the row is `active`, at which point it
 *                        fills solid.
 *   active   {boolean} — highlighted state (modal tabs only)
 *   onClick  {function} — if set, the row becomes a button (modal tabs only)
 */
export default function RankedListRow({ rank, label, progress, value, badge, valueColor, color, active = false, onClick }) {
  const interactive = typeof onClick === 'function'
  const Tag = interactive ? 'button' : 'div'

  return (
    <Tag
      type={interactive ? 'button' : undefined}
      className={`${styles.row} ${active ? styles.active : ''} ${interactive ? styles.interactive : ''}`}
      onClick={onClick}
    >
      <span
        className={styles.rank}
        style={color
          ? (active
            ? { backgroundColor: color, borderColor: color, color: '#ffffff' }
            : { backgroundColor: 'transparent', borderColor: color, color })
          : undefined}
      >
        {rank}
      </span>
      <div className={styles.body}>
        <span className={styles.label}>{label}</span>
        {typeof progress === 'number' && (
          <span className={styles.track}>
            <span
              className={styles.fill}
              style={{ width: `${Math.max(0, Math.min(100, progress))}%`, ...(color ? { backgroundColor: color } : null) }}
            />
          </span>
        )}
      </div>
      {badge
        ? <span className={styles.badge}>{badge}</span>
        : value != null && <span className={styles.value} style={valueColor ? { color: valueColor } : undefined}>{value}</span>}
    </Tag>
  )
}
